import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData } from '@tanstack/vue-query'

import { EventsService } from '../services/events.service'
import { eventsQueryKeys } from '../utils/query-keys.utils'
import {
  EVENTS_ATTENDEES_PAGE_LIMIT,
  EVENTS_CONNECTION_PARTICIPANTS_LIMIT,
  EVENTS_CONNECTION_STATUSES_LIMIT,
  EVENTS_EXHIBITORS_PAGE_LIMIT,
  EVENTS_LEAD_CONTACTS_PAGE_LIMIT,
  EVENTS_PORTAL_LIST_LIMIT,
  type EventsLeadContactsQuery,
  type EventsManagementLeadContactsResponse,
  type EventsManagementLeadExportParams,
  type EventsManagementAdInteractionParams,
  type EventsManagementAdvertisementsResponse,
  type EventsManagementAttendeesResponse,
  type EventsManagementDetailResponse,
  type EventsManagementExhibitor,
  type EventsManagementExhibitorBookmarkParams,
  type EventsManagementExhibitorMembershipsResponse,
  type EventsManagementExhibitorResponse,
  type EventsManagementExhibitorUpdateParams,
  type EventsManagementExhibitorsResponse,
  type EventsManagementMyParticipantResponse,
  type EventsManagementUserProfileResponse,
  type EventsManagementProfileBooth,
  type EventsManagementSavedSessionParams,
  type EventsManagementScheduleResponse,
  type EventsManagementSpeakersResponse,
  type EventsManagementSponsorsResponse,
  type EventsPortalState,
  type EventsPortalStatePage,
  type EventParticipantConnectionAction,
  type EventParticipantReference,
  type EventParticipantRole,
  type EventConnectionStatus,
  type RequestEventConnectionParams,
  type DecideEventConnectionParams,
} from '../types/events-management'

const LISTING_STALE_TIME = 5 * 60 * 1000

/**
 * Listing factories are the swap point for dedicated live / upcoming / past APIs.
 * Today they call `/internal/portal/events?state=` per listing.
 */
export const useEventsQueryStore = (contactId: MaybeRefOrGetter<string | null>) => {
  const service = new EventsService()
  const queryClient = useQueryClient()
  const queryKeys = eventsQueryKeys

  const fetchListingQuery = (listing: EventsPortalState, limit: MaybeRefOrGetter<number> = EVENTS_PORTAL_LIST_LIMIT) => {
    return useQuery<EventsPortalStatePage, Error>({
      queryKey: computed(() => queryKeys.listing(listing, toValue(contactId)?.trim() || undefined, toValue(limit))),
      queryFn: () => service.getPortalEvents({ state: listing, page: 1, limit: toValue(limit) }),
      staleTime: LISTING_STALE_TIME,
      enabled: computed(() => Boolean(toValue(contactId)?.trim())),
    })
  }

  const fetchLiveEventsQuery = (limit: MaybeRefOrGetter<number> = EVENTS_PORTAL_LIST_LIMIT) => fetchListingQuery('live', limit)

  const fetchUpcomingEventsQuery = (limit: MaybeRefOrGetter<number> = EVENTS_PORTAL_LIST_LIMIT) => fetchListingQuery('upcoming', limit)

  const fetchPastEventsQuery = (limit: MaybeRefOrGetter<number> = EVENTS_PORTAL_LIST_LIMIT) => fetchListingQuery('past', limit)

  const fetchEventDetailQuery = (eventId: MaybeRefOrGetter<string | undefined>) => {
    return useQuery<EventsManagementDetailResponse, Error>({
      queryKey: computed(() => queryKeys.detail(toValue(eventId))),
      queryFn: async () => {
        const resolvedEventId = toValue(eventId)?.trim()
        if (!resolvedEventId) {
          throw new Error('Event ID is required for fetching event detail')
        }
        return service.getPortalEvent(resolvedEventId)
      },
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => Boolean(toValue(eventId)?.trim())),
    })
  }

  /** `includeSaved` merges the viewer's saved sessions; only a participant (attendee or speaker) has any. */
  const fetchEventScheduleQuery = (
    eventId: MaybeRefOrGetter<string | undefined>,
    includeSaved: MaybeRefOrGetter<boolean> = false,
    enabled: MaybeRefOrGetter<boolean> = true
  ) => {
    return useQuery<EventsManagementScheduleResponse, Error>({
      queryKey: computed(() => queryKeys.schedule(toValue(eventId), toValue(contactId)?.trim() || undefined, toValue(includeSaved))),
      queryFn: async () => {
        const resolvedEventId = toValue(eventId)?.trim()
        if (!resolvedEventId) {
          throw new Error('Event ID is required for fetching event schedule')
        }
        const [schedule, savedSessions] = await Promise.all([
          service.getPublicEventSchedule(resolvedEventId),
          toValue(includeSaved) ? service.getSavedEventSessions(resolvedEventId) : undefined,
        ])
        const saved = new Set(savedSessions?.items.flatMap(item => item.sessions.map(session => session.sessionId)) ?? [])
        return { ...schedule, sessions: schedule.sessions.map(session => ({ ...session, saved: saved.has(session.id) })) }
      },
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => Boolean(toValue(eventId)?.trim()) && toValue(enabled) && Boolean(toValue(contactId)?.trim())),
    })
  }

  const fetchEventSpeakersQuery = (eventId: MaybeRefOrGetter<string | undefined>, enabled: MaybeRefOrGetter<boolean> = true) => {
    return useQuery<EventsManagementSpeakersResponse, Error>({
      queryKey: computed(() => queryKeys.speakers(toValue(eventId))),
      queryFn: async () => {
        const resolvedEventId = toValue(eventId)?.trim()
        if (!resolvedEventId) {
          throw new Error('Event ID is required for fetching event speakers')
        }
        return service.getPublicEventSpeakers(resolvedEventId)
      },
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => Boolean(toValue(eventId)?.trim()) && toValue(enabled)),
    })
  }

  /**
   * A placement is a built-in tab id or a custom section id. A custom tab's placement is only known once it opens, so
   * an empty placement keeps the query idle.
   */
  /** `options.retry` lets a caller that waits on the ad (the overview) fail fast instead of retrying with backoff. */
  const fetchEventAdvertisementsQuery = (
    eventId: MaybeRefOrGetter<string | undefined>,
    placement: MaybeRefOrGetter<string>,
    options: { retry?: boolean | number } = {}
  ) => {
    return useQuery<EventsManagementAdvertisementsResponse, Error>({
      ...(options.retry !== undefined ? { retry: options.retry } : {}),
      queryKey: computed(() => queryKeys.advertisements(toValue(eventId), toValue(placement))),
      queryFn: async () => {
        const resolvedEventId = toValue(eventId)?.trim()
        const resolvedPlacement = toValue(placement).trim()
        if (!resolvedEventId) {
          throw new Error('Event ID is required for fetching event advertisements')
        }
        if (!resolvedPlacement) {
          throw new Error('Placement is required for fetching event advertisements')
        }
        return service.getPublicEventAdvertisements({ eventId: resolvedEventId, placement: resolvedPlacement })
      },
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => Boolean(toValue(eventId)?.trim()) && Boolean(toValue(placement).trim())),
    })
  }

  const fetchEventSponsorsQuery = (eventId: MaybeRefOrGetter<string | undefined>, enabled: MaybeRefOrGetter<boolean> = true) => {
    return useQuery<EventsManagementSponsorsResponse, Error>({
      queryKey: computed(() => queryKeys.sponsors(toValue(eventId))),
      queryFn: async () => {
        const resolvedEventId = toValue(eventId)?.trim()
        if (!resolvedEventId) {
          throw new Error('Event ID is required for fetching event sponsors')
        }
        return service.getPublicEventSponsors(resolvedEventId)
      },
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => Boolean(toValue(eventId)?.trim()) && toValue(enabled)),
    })
  }

  /** Attendees are only visible to a registered viewer; `enabled` keeps the request from firing otherwise. */
  const fetchEventAttendeesQuery = (
    eventId: MaybeRefOrGetter<string | undefined>,
    page: MaybeRefOrGetter<number | undefined> = 1,
    search: MaybeRefOrGetter<string | undefined> = undefined,
    enabled: MaybeRefOrGetter<boolean> = true
  ) => {
    const resolvedPage = () => {
      const value = toValue(page)
      return typeof value === 'number' && value >= 1 ? Math.floor(value) : 1
    }
    const resolvedSearch = () => {
      const value = toValue(search)
      const trimmed = typeof value === 'string' ? value.trim() : ''
      return trimmed || undefined
    }

    return useQuery<EventsManagementAttendeesResponse, Error>({
      queryKey: computed(() => queryKeys.attendees(toValue(eventId), resolvedPage(), resolvedSearch())),
      queryFn: async () => {
        const resolvedEventId = toValue(eventId)?.trim()
        if (!resolvedEventId) {
          throw new Error('Event ID is required for fetching event attendees')
        }
        const search = resolvedSearch()
        return service.getEventAttendees(resolvedEventId, {
          page: resolvedPage(),
          limit: EVENTS_ATTENDEES_PAGE_LIMIT,
          ...(search ? { search } : {}),
        })
      },
      placeholderData: keepPreviousData,
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => Boolean(toValue(eventId)?.trim()) && toValue(enabled) && Boolean(toValue(contactId)?.trim())),
    })
  }

  /** The viewer's own participant on the event, keyed on the viewer because the portal resolves it from auth. */
  const fetchMyEventParticipantQuery = (eventId: MaybeRefOrGetter<string | undefined>, enabled: MaybeRefOrGetter<boolean> = true) => {
    return useQuery<EventsManagementMyParticipantResponse | null, Error>({
      queryKey: computed(() => queryKeys.myParticipant(toValue(eventId), toValue(contactId)?.trim() || undefined)),
      queryFn: async () => {
        const resolvedEventId = toValue(eventId)?.trim()
        if (!resolvedEventId) {
          throw new Error('Event ID is required for fetching the viewer participant')
        }
        return service.getMyEventParticipant(resolvedEventId)
      },
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => Boolean(toValue(eventId)?.trim()) && toValue(enabled) && Boolean(toValue(contactId)?.trim())),
    })
  }

  /** Without `role` the id is a portal account id; with one it is that participant's id. */
  const fetchEventUserQuery = (
    eventId: MaybeRefOrGetter<string | undefined>,
    id: MaybeRefOrGetter<string | undefined>,
    role?: MaybeRefOrGetter<EventParticipantRole | undefined>
  ) => {
    return useQuery<EventsManagementUserProfileResponse, Error>({
      queryKey: computed(() => queryKeys.user(toValue(eventId), toValue(id)?.trim() || undefined, toValue(role))),
      queryFn: async () => {
        const resolvedEventId = toValue(eventId)?.trim()
        const resolvedId = toValue(id)?.trim()
        if (!resolvedEventId) {
          throw new Error('Event ID is required for fetching a person')
        }
        if (!resolvedId) {
          throw new Error('Person ID is required for fetching a person')
        }
        const resolvedRole = toValue(role)
        return service.getEventUser({ eventId: resolvedEventId, id: resolvedId, ...(resolvedRole ? { role: resolvedRole } : {}) })
      },
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => Boolean(toValue(eventId)?.trim()) && Boolean(toValue(id)?.trim())),
    })
  }

  const fetchEventConnectionActionsQuery = (
    eventId: MaybeRefOrGetter<string | undefined>,
    participants: MaybeRefOrGetter<readonly EventParticipantReference[]>,
    isRegistered: MaybeRefOrGetter<boolean> = true
  ) => {
    return useQuery<readonly EventParticipantConnectionAction[], Error>({
      queryKey: computed(() => {
        const participantKeys = toValue(participants).map(participant => `${participant.role}:${participant.participantId}`)
        return queryKeys.connectionActions(toValue(eventId), toValue(contactId)?.trim() || undefined, participantKeys)
      }),
      queryFn: async () => {
        const resolvedEventId = toValue(eventId)?.trim()
        const resolvedParticipants = toValue(participants)
        if (!resolvedEventId) throw new Error('Event ID is required for connection actions')
        return service.getEventConnectionActions({ eventId: resolvedEventId, participants: resolvedParticipants })
      },
      staleTime: 30 * 1000,
      enabled: computed(() => {
        const count = toValue(participants).length
        return (
          toValue(isRegistered) &&
          Boolean(toValue(eventId)?.trim()) &&
          count > 0 &&
          count <= EVENTS_CONNECTION_PARTICIPANTS_LIMIT &&
          Boolean(toValue(contactId)?.trim())
        )
      }),
    })
  }

  const fetchEventConnectionStatusesQuery = (connectionIds: MaybeRefOrGetter<readonly string[]>) => {
    return useQuery<readonly EventConnectionStatus[], Error>({
      queryKey: computed(() => queryKeys.connectionStatuses(toValue(contactId)?.trim() || undefined, toValue(connectionIds))),
      queryFn: () => service.getEventConnectionStatuses(toValue(connectionIds)),
      staleTime: 15 * 1000,
      enabled: computed(() => {
        const count = toValue(connectionIds).length
        return count > 0 && count <= EVENTS_CONNECTION_STATUSES_LIMIT && Boolean(toValue(contactId)?.trim())
      }),
    })
  }

  const fetchEventExhibitorsQuery = (eventId: MaybeRefOrGetter<string | undefined>, enabled: MaybeRefOrGetter<boolean> = true) => {
    const infiniteQuery = useInfiniteQuery<
      EventsManagementExhibitorsResponse,
      Error,
      InfiniteData<EventsManagementExhibitorsResponse, number>,
      readonly unknown[],
      number
    >({
      queryKey: computed(() => queryKeys.exhibitors(toValue(eventId))),
      queryFn: async ({ pageParam }) => {
        const resolvedEventId = toValue(eventId)?.trim()
        if (!resolvedEventId) {
          throw new Error('Event ID is required for fetching exhibitors')
        }
        return service.listExhibitors({ eventId: resolvedEventId, page: pageParam, limit: EVENTS_EXHIBITORS_PAGE_LIMIT })
      },
      initialPageParam: 1,
      getNextPageParam: lastPage => (lastPage.pagination.hasMore ? lastPage.pagination.page + 1 : undefined),
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => Boolean(toValue(eventId)?.trim()) && toValue(enabled)),
    })

    const allData = computed(() => infiniteQuery.data.value?.pages.flatMap(page => page.exhibitors) ?? [])

    return {
      ...infiniteQuery,
      allData,
    }
  }

  /** One exhibitor with its team, for the profile. */
  const fetchEventExhibitorQuery = (eventId: MaybeRefOrGetter<string | undefined>, exhibitorId: MaybeRefOrGetter<string | undefined>) => {
    return useQuery<EventsManagementExhibitorResponse, Error>({
      queryKey: computed(() => queryKeys.exhibitor(toValue(eventId), toValue(exhibitorId)?.trim() || undefined)),
      queryFn: async () => {
        const resolvedEventId = toValue(eventId)?.trim()
        const resolvedExhibitorId = toValue(exhibitorId)?.trim()
        if (!resolvedEventId) {
          throw new Error('Event ID is required for fetching an exhibitor')
        }
        if (!resolvedExhibitorId) {
          throw new Error('Exhibitor ID is required for fetching an exhibitor')
        }
        return service.getExhibitor({ eventId: resolvedEventId, exhibitorId: resolvedExhibitorId })
      },
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => Boolean(toValue(eventId)?.trim()) && Boolean(toValue(exhibitorId)?.trim())),
    })
  }

  const fetchEventExhibitorMembershipsQuery = (eventId: MaybeRefOrGetter<string | undefined>, enabled?: MaybeRefOrGetter<boolean>) => {
    return useQuery<EventsManagementExhibitorMembershipsResponse, Error>({
      queryKey: computed(() => queryKeys.exhibitorMemberships(toValue(eventId), toValue(contactId)?.trim() || undefined)),
      queryFn: async () => {
        const resolvedEventId = toValue(eventId)?.trim()
        if (!resolvedEventId) throw new Error('Event ID is required for fetching exhibitor memberships')
        return service.listExhibitorMemberships({ eventId: resolvedEventId })
      },
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => Boolean(toValue(eventId)?.trim()) && (toValue(enabled) ?? true) && Boolean(toValue(contactId)?.trim())),
    })
  }

  /** The ids a contacts request needs; the query throws rather than hit the endpoint without them. */
  const resolveLeadContactsIds = (eventId: MaybeRefOrGetter<string | undefined>, exhibitorId: MaybeRefOrGetter<string | undefined>) => {
    const resolvedEventId = toValue(eventId)?.trim()
    const resolvedExhibitorId = toValue(exhibitorId)?.trim()
    if (!resolvedEventId) throw new Error('Event ID is required for fetching exhibitor contacts')
    if (!resolvedExhibitorId) throw new Error('Exhibitor ID is required for fetching exhibitor contacts')
    return { eventId: resolvedEventId, exhibitorId: resolvedExhibitorId }
  }

  const isLeadContactsEnabled = (
    eventId: MaybeRefOrGetter<string | undefined>,
    exhibitorId: MaybeRefOrGetter<string | undefined>,
    enabled: MaybeRefOrGetter<boolean>
  ) => Boolean(toValue(eventId)?.trim()) && Boolean(toValue(exhibitorId)?.trim()) && toValue(enabled) && Boolean(toValue(contactId)?.trim())

  /** One page of the exhibitor's contacts, for the paginated table; the previous page stays up while the next loads. */
  const fetchExhibitorLeadContactsPageQuery = (
    eventId: MaybeRefOrGetter<string | undefined>,
    exhibitorId: MaybeRefOrGetter<string | undefined>,
    query: MaybeRefOrGetter<EventsLeadContactsQuery>,
    enabled: MaybeRefOrGetter<boolean> = true
  ) => {
    return useQuery<EventsManagementLeadContactsResponse, Error>({
      queryKey: computed(() => {
        const { search, order, page } = toValue(query)
        return queryKeys.leadContacts(toValue(eventId), toValue(exhibitorId)?.trim() || undefined, search, order, page)
      }),
      queryFn: () => {
        const { search, order, page } = toValue(query)
        return service.listExhibitorLeadContacts({
          ...resolveLeadContactsIds(eventId, exhibitorId),
          page,
          pageLimit: EVENTS_LEAD_CONTACTS_PAGE_LIMIT,
          search,
          order,
        })
      },
      placeholderData: keepPreviousData,
      staleTime: 60 * 1000,
      enabled: computed(() => isLeadContactsEnabled(eventId, exhibitorId, enabled)),
    })
  }

  /** The exhibitor's contacts loaded page by page, for the compact list's infinite scroll. */
  const fetchExhibitorLeadContactsInfiniteQuery = (
    eventId: MaybeRefOrGetter<string | undefined>,
    exhibitorId: MaybeRefOrGetter<string | undefined>,
    query: MaybeRefOrGetter<EventsLeadContactsQuery>,
    enabled: MaybeRefOrGetter<boolean> = true
  ) => {
    const infiniteQuery = useInfiniteQuery<
      EventsManagementLeadContactsResponse,
      Error,
      InfiniteData<EventsManagementLeadContactsResponse, number>,
      readonly unknown[],
      number
    >({
      queryKey: computed(() => {
        const { search, order } = toValue(query)
        return queryKeys.leadContactsInfinite(toValue(eventId), toValue(exhibitorId)?.trim() || undefined, search, order)
      }),
      queryFn: ({ pageParam }) => {
        const { search, order } = toValue(query)
        return service.listExhibitorLeadContacts({
          ...resolveLeadContactsIds(eventId, exhibitorId),
          page: pageParam,
          pageLimit: EVENTS_LEAD_CONTACTS_PAGE_LIMIT,
          search,
          order,
        })
      },
      initialPageParam: 1,
      getNextPageParam: lastPage => (lastPage.pagination.hasMore ? lastPage.pagination.page + 1 : undefined),
      staleTime: 60 * 1000,
      enabled: computed(() => isLeadContactsEnabled(eventId, exhibitorId, enabled)),
    })

    const allData = computed(() => infiniteQuery.data.value?.pages.flatMap(page => page.contacts) ?? [])

    return {
      ...infiniteQuery,
      allData,
    }
  }

  /** A one-off file download, not cached: every export reflects the leads as they are now. */
  const exportExhibitorLeads = (params: EventsManagementLeadExportParams) => service.exportExhibitorLeads(params)

  const withBookmark = (exhibitor: EventsManagementExhibitor, variables: EventsManagementExhibitorBookmarkParams) =>
    exhibitor.id === variables.exhibitorId ? { ...exhibitor, bookmarked: variables.bookmarked } : exhibitor

  const withBoothBookmark = (booth: EventsManagementProfileBooth, variables: EventsManagementExhibitorBookmarkParams) =>
    booth.id === variables.exhibitorId ? { ...booth, bookmarked: variables.bookmarked } : booth

  // Optimistic: the directory row, the open profile and any user profile listing the booth flip at once, and all
  // roll back if the request fails.
  const toggleExhibitorBookmarkMutation = useMutation({
    mutationFn: (variables: EventsManagementExhibitorBookmarkParams) => service.updateExhibitorBookmark(variables),
    onMutate: async variables => {
      const exhibitorsKey = queryKeys.exhibitors(variables.eventId)
      const exhibitorKey = queryKeys.exhibitor(variables.eventId, variables.exhibitorId)
      const usersKey = ['events', 'user', variables.eventId]
      await Promise.all([
        queryClient.cancelQueries({ queryKey: exhibitorsKey }),
        queryClient.cancelQueries({ queryKey: exhibitorKey }),
        queryClient.cancelQueries({ queryKey: usersKey }),
      ])
      const previousExhibitors = queryClient.getQueryData<InfiniteData<EventsManagementExhibitorsResponse, number>>(exhibitorsKey)
      const previousExhibitor = queryClient.getQueryData<EventsManagementExhibitorResponse>(exhibitorKey)
      const previousUsers = queryClient.getQueriesData<EventsManagementUserProfileResponse>({ queryKey: usersKey })
      queryClient.setQueriesData<EventsManagementUserProfileResponse>({ queryKey: usersKey }, profile =>
        profile ? { ...profile, memberOf: profile.memberOf.map(booth => withBoothBookmark(booth, variables)) } : profile
      )
      if (previousExhibitors) {
        queryClient.setQueryData<InfiniteData<EventsManagementExhibitorsResponse, number>>(exhibitorsKey, {
          ...previousExhibitors,
          pages: previousExhibitors.pages.map(page => ({
            ...page,
            exhibitors: page.exhibitors.map(exhibitor => withBookmark(exhibitor, variables)),
          })),
        })
      }
      if (previousExhibitor?.exhibitor) {
        queryClient.setQueryData<EventsManagementExhibitorResponse>(exhibitorKey, {
          ...previousExhibitor,
          exhibitor: withBookmark(previousExhibitor.exhibitor, variables),
        })
      }
      return { previousExhibitors, previousExhibitor, previousUsers, exhibitorsKey, exhibitorKey }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousExhibitors) queryClient.setQueryData(context.exhibitorsKey, context.previousExhibitors)
      if (context?.previousExhibitor) queryClient.setQueryData(context.exhibitorKey, context.previousExhibitor)
      context?.previousUsers.forEach(([queryKey, profile]) => queryClient.setQueryData(queryKey, profile))
    },
  })

  const updateExhibitorMutation = useMutation({
    mutationFn: (variables: EventsManagementExhibitorUpdateParams) => service.updateExhibitor(variables),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.exhibitor(variables.eventId, variables.exhibitorId),
        }),
        queryClient.invalidateQueries({ queryKey: queryKeys.exhibitors(variables.eventId) }),
      ])
    },
  })

  const updateSavedSessionMutation = useMutation({
    mutationFn: (variables: EventsManagementSavedSessionParams) => service.updateSavedSession(variables),
    onMutate: async variables => {
      // Saving is registered-only, so the cached schedule is the one that includes saved sessions.
      const queryKey = queryKeys.schedule(variables.eventId, toValue(contactId)?.trim() || undefined, true)
      // A user profile lists the same sessions, so its saved flags flip with the schedule's.
      const usersKey = ['events', 'user', variables.eventId]
      await Promise.all([queryClient.cancelQueries({ queryKey }), queryClient.cancelQueries({ queryKey: usersKey })])
      const withSaved = <TSession extends { readonly id: string }>(session: TSession) =>
        session.id === variables.sessionId ? { ...session, saved: variables.saved } : session
      const previous = queryClient.getQueryData<EventsManagementScheduleResponse>(queryKey)
      if (previous) {
        queryClient.setQueryData<EventsManagementScheduleResponse>(queryKey, { ...previous, sessions: previous.sessions.map(withSaved) })
      }
      const previousUsers = queryClient.getQueriesData<EventsManagementUserProfileResponse>({ queryKey: usersKey })
      queryClient.setQueriesData<EventsManagementUserProfileResponse>({ queryKey: usersKey }, profile =>
        profile ? { ...profile, speakingAt: profile.speakingAt.map(withSaved), attending: profile.attending.map(withSaved) } : profile
      )
      return { previous, queryKey, previousUsers }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous && context.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previous)
      }
      context?.previousUsers.forEach(([userKey, profile]) => queryClient.setQueryData(userKey, profile))
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.schedule(variables.eventId, toValue(contactId)?.trim() || undefined, true),
      })
      queryClient.invalidateQueries({ queryKey: ['events', 'user', variables.eventId] })
    },
  })

  const invalidateConnectionState = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['events', 'connectionActions'] }),
      queryClient.invalidateQueries({ queryKey: ['events', 'connectionStatuses'] }),
      // A user profile carries the viewer's connection action towards that person.
      queryClient.invalidateQueries({ queryKey: ['events', 'user'] }),
    ])
  }

  const requestEventConnectionMutation = useMutation({
    mutationFn: (variables: RequestEventConnectionParams) => service.requestEventConnection(variables),
    onSuccess: invalidateConnectionState,
  })

  const acceptEventConnectionMutation = useMutation({
    mutationFn: (variables: DecideEventConnectionParams) => service.acceptEventConnection(variables),
    onSuccess: invalidateConnectionState,
  })

  const declineEventConnectionMutation = useMutation({
    mutationFn: (variables: DecideEventConnectionParams) => service.declineEventConnection(variables),
    onSuccess: invalidateConnectionState,
  })

  const trackAdvertisementInteractionMutation = useMutation({
    mutationFn: (variables: EventsManagementAdInteractionParams) => service.trackAdvertisementInteraction(variables),
  })

  return {
    fetchLiveEventsQuery,
    fetchUpcomingEventsQuery,
    fetchPastEventsQuery,
    fetchEventDetailQuery,
    fetchEventScheduleQuery,
    fetchEventSpeakersQuery,
    fetchEventSponsorsQuery,
    fetchEventAdvertisementsQuery,
    fetchEventAttendeesQuery,
    fetchEventUserQuery,
    fetchMyEventParticipantQuery,
    fetchEventConnectionActionsQuery,
    fetchEventConnectionStatusesQuery,
    fetchEventExhibitorsQuery,
    fetchEventExhibitorQuery,
    fetchEventExhibitorMembershipsQuery,
    fetchExhibitorLeadContactsPageQuery,
    fetchExhibitorLeadContactsInfiniteQuery,
    exportExhibitorLeads,
    toggleExhibitorBookmark: toggleExhibitorBookmarkMutation.mutateAsync,
    updateExhibitor: updateExhibitorMutation.mutateAsync,
    updateSavedSession: updateSavedSessionMutation.mutateAsync,
    requestEventConnection: requestEventConnectionMutation.mutateAsync,
    acceptEventConnection: acceptEventConnectionMutation.mutateAsync,
    declineEventConnection: declineEventConnectionMutation.mutateAsync,
    trackAdvertisementInteraction: trackAdvertisementInteractionMutation.mutateAsync,
    queryKeys,
  }
}
